import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';

/**
 * ExecutionHistoryControls Component
 *
 * Displays pagination and sorting controls for the execution history.
 *
 * @param {Object} props
 * @param {number} props.page - Current page number
 * @param {number} props.pageSize - Current page size
 * @param {number} props.totalCount - Total count of executions
 * @param {number} props.totalPages - Total number of pages
 * @param {string} props.sortBy - Current sort column
 * @param {string} props.sortOrder - Current sort order (asc/desc)
 * @param {Function} props.onPageSizeChange - Callback when page size changes
 * @param {Function} props.onSortChange - Callback when sort column changes
 * @param {Function} props.onSortOrderToggle - Callback to toggle sort order
 * @param {Function} props.onPageChange - Callback when page changes
 */
function ExecutionHistoryControls({
  page,
  pageSize,
  totalCount,
  totalPages,
  sortBy,
  sortOrder,
  onPageSizeChange,
  onSortChange,
  onSortOrderToggle,
  onPageChange,
}) {
  const handleChangePage = (event, newPage) => {
    if (onPageChange) {
      onPageChange(newPage + 1); // API uses 1-based page numbers
    }
  };

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        backgroundColor: 'grey.50',
        borderRadius: 1,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        flexWrap: 'wrap',
        border: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      {/* Pagination Info */}
      <Typography variant="body2" color="text.secondary">
        Showing {(page - 1) * pageSize + 1}-
        {Math.min(page * pageSize, totalCount)} of {totalCount} executions
      </Typography>

      {/* Rows Per Page Selector */}
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Rows</InputLabel>
        <Select value={pageSize} label="Rows" onChange={onPageSizeChange}>
          <MenuItem value={5}>5</MenuItem>
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={25}>25</MenuItem>
          <MenuItem value={50}>50</MenuItem>
        </Select>
      </FormControl>

      {/* Sort Controls */}
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Sort By</InputLabel>
        <Select value={sortBy} label="Sort By" onChange={onSortChange}>
          <MenuItem value="created_at">Created Date</MenuItem>
          <MenuItem value="execution_time">Execution Time</MenuItem>
          <MenuItem value="cost">Cost</MenuItem>
          <MenuItem value="model_used">Model</MenuItem>
          <MenuItem value="provider_used">Provider</MenuItem>
        </Select>
      </FormControl>

      <Button size="small" onClick={onSortOrderToggle}>
        {sortOrder === 'desc' ? '↓ Descending' : '↑ Ascending'}
      </Button>

      {/* Page Navigation */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          size="small"
          disabled={page <= 1}
          onClick={() => handleChangePage(null, 0)}
        >
          First
        </Button>
        <Button
          size="small"
          disabled={page <= 1}
          onClick={() => handleChangePage(null, page - 2)}
        >
          Previous
        </Button>
        <Typography variant="body2" sx={{ mx: 1 }}>
          Page {page} of {totalPages}
        </Typography>
        <Button
          size="small"
          disabled={page >= totalPages}
          onClick={() => handleChangePage(null, page)}
        >
          Next
        </Button>
        <Button
          size="small"
          disabled={page >= totalPages}
          onClick={() => handleChangePage(null, totalPages - 1)}
        >
          Last
        </Button>
      </Box>
    </Box>
  );
}

export default ExecutionHistoryControls;
